import { motion } from 'framer-motion';
import {Sparkles, Users, Compass, Book, Star, Flame, Droplet, Wind, Mountain} from 'lucide-react';
import { Link } from 'react-router-dom';

export function ZodiacSignsRisingSign() {
  const risingSignsData = [
    {
      sign: 'Aries Rising',
      element: 'Fire',
      ruler: 'Mars',
      appearance: 'Athletic build, prominent forehead, strong eyebrows, energetic presence',
      personality: 'Bold, direct, pioneering. You approach life head-first with courage and initiative. First impressions: confident, assertive, sometimes impulsive.',
      strengths: 'Leadership, courage, enthusiasm, competitive drive',
      challenges: 'Impatience, aggression, rushing into situations',
      icon: <Flame className="w-6 h-6" />
    },
    {
      sign: 'Taurus Rising',
      element: 'Earth',
      ruler: 'Venus',
      appearance: 'Solid build, beautiful eyes/neck, soft features, graceful movements',
      personality: 'Steady, sensual, grounded. You exude calm reliability and physical presence. First impressions: stable, pleasant, slow to warm up.',
      strengths: 'Patience, determination, appreciation for beauty, loyalty',
      challenges: 'Stubbornness, resistance to change, materialism',
      icon: <Mountain className="w-6 h-6" />
    },
    {
      sign: 'Gemini Rising',
      element: 'Air',
      ruler: 'Mercury',
      appearance: 'Youthful face, expressive hands, quick movements, sparkling eyes',
      personality: 'Curious, adaptable, communicative. You greet the world with questions and wit. First impressions: intelligent, chatty, restless.',
      strengths: 'Versatility, communication skills, quick thinking, social charm',
      challenges: 'Inconsistency, superficiality, nervous energy',
      icon: <Wind className="w-6 h-6" />
    },
    {
      sign: 'Cancer Rising',
      element: 'Water',
      ruler: 'Moon',
      appearance: 'Round face, expressive eyes, nurturing demeanor, soft features',
      personality: 'Protective, intuitive, emotional. You approach life cautiously and empathetically. First impressions: caring, sensitive, moody.',
      strengths: 'Empathy, nurturing abilities, emotional intelligence, intuition',
      challenges: 'Over-sensitivity, defensiveness, emotional volatility',
      icon: <Droplet className="w-6 h-6" />
    },
    {
      sign: 'Leo Rising',
      element: 'Fire',
      ruler: 'Sun',
      appearance: 'Mane-like hair, regal posture, warm smile, dramatic presence',
      personality: 'Confident, charismatic, expressive. You shine naturally and command attention. First impressions: warm, generous, theatrical.',
      strengths: 'Charisma, creativity, generosity, natural leadership',
      challenges: 'Pride, need for validation, dramatic reactions',
      icon: <Flame className="w-6 h-6" />
    },
    {
      sign: 'Virgo Rising',
      element: 'Earth',
      ruler: 'Mercury',
      appearance: 'Delicate features, neat appearance, observant eyes, precise movements',
      personality: 'Analytical, helpful, perfectionistic. You approach life with discernment and service. First impressions: modest, intelligent, critical.',
      strengths: 'Attention to detail, analytical mind, practical skills, helpful nature',
      challenges: 'Perfectionism, worry, hyper-criticism',
      icon: <Mountain className="w-6 h-6" />
    },
    {
      sign: 'Libra Rising',
      element: 'Air',
      ruler: 'Venus',
      appearance: 'Symmetrical features, charming smile, elegant style, balanced proportions',
      personality: 'Diplomatic, charming, relationship-oriented. You seek harmony and beauty in all interactions. First impressions: pleasant, indecisive, fair.',
      strengths: 'Diplomacy, aesthetic sense, social grace, partnership focus',
      challenges: 'Indecisiveness, people-pleasing, conflict avoidance',
      icon: <Wind className="w-6 h-6" />
    },
    {
      sign: 'Scorpio Rising',
      element: 'Water',
      ruler: 'Pluto/Mars',
      appearance: 'Intense eyes, magnetic presence, penetrating gaze, mysterious aura',
      personality: 'Intense, secretive, powerful. You approach life with depth and transformation. First impressions: mysterious, intimidating, magnetic.',
      strengths: 'Depth of emotion, investigative skills, transformative power, resilience',
      challenges: 'Jealousy, controlling tendencies, secrecy',
      icon: <Droplet className="w-6 h-6" />
    },
    {
      sign: 'Sagittarius Rising',
      element: 'Fire',
      ruler: 'Jupiter',
      appearance: 'Athletic build, long limbs, optimistic expression, open face',
      personality: 'Adventurous, philosophical, optimistic. You greet life with enthusiasm and broad vision. First impressions: friendly, blunt, energetic.',
      strengths: 'Optimism, philosophical mind, adventurous spirit, honesty',
      challenges: 'Over-commitment, bluntness, restlessness',
      icon: <Flame className="w-6 h-6" />
    },
    {
      sign: 'Capricorn Rising',
      element: 'Earth',
      ruler: 'Saturn',
      appearance: 'Strong bone structure, serious expression, mature demeanor, ages well',
      personality: 'Ambitious, disciplined, reserved. You approach life with structure and long-term vision. First impressions: mature, competent, serious.',
      strengths: 'Discipline, ambition, responsibility, strategic thinking',
      challenges: 'Pessimism, rigidity, workaholism',
      icon: <Mountain className="w-6 h-6" />
    },
    {
      sign: 'Aquarius Rising',
      element: 'Air',
      ruler: 'Uranus/Saturn',
      appearance: 'Unique features, unconventional style, intelligent eyes, eccentric vibe',
      personality: 'Unconventional, humanitarian, detached. You greet the world with innovation and individuality. First impressions: quirky, friendly, aloof.',
      strengths: 'Innovation, humanitarian values, independence, intellectual prowess',
      challenges: 'Emotional detachment, rebelliousness, unpredictability',
      icon: <Wind className="w-6 h-6" />
    },
    {
      sign: 'Pisces Rising',
      element: 'Water',
      ruler: 'Neptune/Jupiter',
      appearance: 'Dreamy eyes, soft features, fluid movements, ethereal presence',
      personality: 'Compassionate, imaginative, intuitive. You approach life with empathy and spirituality. First impressions: gentle, artistic, elusive.',
      strengths: 'Compassion, artistic talent, spiritual awareness, adaptability',
      challenges: 'Boundary issues, escapism, confusion',
      icon: <Droplet className="w-6 h-6" />
    }
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-16"
      >
        <div className="inline-block mb-4 px-4 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-medium tracking-wide">
          Complete Guide to All 12 Rising Signs
        </div>
        <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight">
          Zodiac Signs Rising Sign: <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FCD34D]">The Complete Encyclopedia</span>
        </h1>
        <p className="text-xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
          Your zodiac signs rising sign (Ascendant) is the mask you wear to the world. Unlike your Sun sign, which represents your core self, your rising sign shapes your physical appearance, first impressions, and how you navigate life's opportunities. Discover all 12 zodiac signs rising sign characteristics below.
        </p>
      </motion.div>

      {/* What are Zodiac Signs Rising Sign */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-gradient-to-br from-[#131722] to-[#0B0E14] p-8 rounded-2xl border border-white/10">
          <h2 className="text-3xl font-serif font-bold text-white mb-6 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-[#D4AF37]" />
            What Are Zodiac Signs Rising Sign?
          </h2>
          <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
            <p>
              The term <strong className="text-[#D4AF37]">"zodiac signs rising sign"</strong> refers to the specific zodiac constellation that was ascending on the eastern horizon at your exact moment of birth. While there are 12 zodiac signs (Aries through Pisces), your personal <strong className="text-white">rising sign</strong> is determined by your birth time and location—not just your birthday.
            </p>
            <p>
              Each of the 12 zodiac signs can be a rising sign, creating 144 unique Sun-Rising sign combinations (12 Sun signs × 12 Rising signs). This is why two people born on the same day can have completely different life experiences and personalities—their <strong className="text-[#D4AF37]">zodiac signs rising sign</strong> filters their Sun sign energy differently.
            </p>
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-yellow-500/10 border border-[#D4AF37]/30 p-6 rounded-xl mt-6">
              <p className="text-[#D4AF37] font-semibold mb-2">🔑 Critical Distinction:</p>
              <p className="text-gray-200 text-base">
                <strong>Sun Sign</strong> = Who you ARE (core identity)<br/>
                <strong>Rising Sign</strong> = How you APPEAR (outward personality, physical body, life path)<br/>
                <strong>Moon Sign</strong> = How you FEEL (emotions, inner world)
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Why Zodiac Signs Rising Sign Matters */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <h2 className="text-3xl font-serif font-bold text-white mb-8 text-center">
          Why Your Zodiac Signs Rising Sign Matters More Than You Think
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ImportanceCard
            icon={<Users className="w-10 h-10 text-purple-400" />}
            title="First Impressions"
            description="Your rising sign governs how strangers perceive you before they know you. It's your 'social mask' and public persona."
          />
          <ImportanceCard
            icon={<Compass className="w-10 h-10 text-blue-400" />}
            title="Life Path Direction"
            description="Your rising sign sets the structure of your 12 astrological houses, determining which life areas are emphasized."
          />
          <ImportanceCard
            icon={<Book className="w-10 h-10 text-green-400" />}
            title="Physical Body"
            description="Ancient astrologers believed your rising sign influences physical appearance, health tendencies, and energy levels."
          />
        </div>
      </motion.section>

      {/* The 12 Zodiac Signs Rising Sign */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <h2 className="text-4xl font-serif font-bold text-white mb-4 text-center">
          The 12 Zodiac Signs Rising Sign: Complete Profiles
        </h2>
        <p className="text-gray-400 text-center mb-10 max-w-3xl mx-auto">
          Each zodiac sign brings unique energy when it becomes your rising sign. Discover how each of the 12 zodiac signs rising sign manifests in personality, appearance, and life approach.
        </p>
        <div className="space-y-6">
          {risingSignsData.map((data, idx) => (
            <RisingSignCard key={idx} data={data} index={idx} />
          ))}
        </div>
      </motion.section>

      {/* How Zodiac Signs Rising Sign Interact with Sun Signs */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 p-8 rounded-2xl border border-purple-500/30">
          <h2 className="text-3xl font-serif font-bold text-white mb-6">
            How Zodiac Signs Rising Sign Interact with Sun Signs
          </h2>
          <div className="space-y-6">
            <InteractionExample
              sunSign="Leo Sun"
              risingSign="Virgo Rising"
              effect="The Leo's natural confidence and need for attention is tempered by Virgo Rising's modesty and precision. Result: A charismatic perfectionist who leads through competence rather than drama."
            />
            <InteractionExample
              sunSign="Aquarius Sun"
              risingSign="Scorpio Rising"
              effect="Aquarius's detached intellectualism gains Scorpio Rising's intensity and magnetism. Result: A powerful, mysterious rebel with deep emotional insight beneath a cool exterior."
            />
            <InteractionExample
              sunSign="Taurus Sun"
              risingSign="Aries Rising"
              effect="Taurus's slow, steady nature is accelerated by Aries Rising's impulsiveness. Result: A determined go-getter who charges toward stability and material goals."
            />
            <InteractionExample
              sunSign="Pisces Sun"
              risingSign="Capricorn Rising"
              effect="Pisces's dreamy, spiritual nature gains Capricorn Rising's structure and ambition. Result: A practical mystic who manifests spiritual visions into tangible achievements."
            />
          </div>
        </div>
      </motion.section>

      {/* How to Calculate Your Zodiac Signs Rising Sign */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-[#131722] p-8 rounded-2xl border border-white/10">
          <h2 className="text-3xl font-serif font-bold text-white mb-6">
            How to Calculate Your Zodiac Signs Rising Sign (3 Methods)
          </h2>
          <div className="space-y-6">
            <MethodCard
              number={1}
              title="Use an Advanced Calculator (Recommended)"
              description="Modern calculators use Swiss Ephemeris data for maximum accuracy. You'll need: exact birth time (within 5 minutes), birth date, and birth city/country. The calculator converts your location to coordinates and calculates which zodiac sign was rising at that precise moment."
              accuracy="99.9% accurate"
            />
            <MethodCard
              number={2}
              title="Consult an Astrologer"
              description="Professional astrologers can hand-calculate your rising sign using ephemeris tables and house system mathematics. They can also rectify your chart if your birth time is uncertain (within 2-hour window)."
              accuracy="100% accurate + interpretation"
            />
            <MethodCard
              number={3}
              title="Manual Calculation (Advanced)"
              description="Requires: Swiss Ephemeris, Table of Houses, accurate birth time converted to GMT. Calculate Local Sidereal Time (LST), then look up corresponding zodiac degree in Table of Houses for your latitude. Not recommended for beginners."
              accuracy="Depends on skill level"
            />
          </div>
        </div>
      </motion.section>

      {/* Common Questions */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <h2 className="text-3xl font-serif font-bold text-white mb-8 text-center">
          Common Questions About Zodiac Signs Rising Sign
        </h2>
        <div className="space-y-4">
          <QuestionCard
            question="Can my Sun sign and Rising sign be the same?"
            answer="Yes! This is called a 'double sign' (e.g., Leo Sun + Leo Rising). Your Sun sign qualities are amplified and very visible to others. You embody that sign's energy strongly."
          />
          <QuestionCard
            question="How often do zodiac signs rising sign change?"
            answer="The rising sign changes approximately every 2 hours (or 1 zodiac sign per 2 hours). This is why exact birth time is critical—a 4-minute difference can change your rising sign."
          />
          <QuestionCard
            question="Is rising sign more important than Sun sign?"
            answer="They're equally important but serve different functions. Your Sun sign is your core identity and life purpose. Your Rising sign is your life path, physical body, and how you experience the world. Ancient astrologers often prioritized the Rising sign."
          />
          <QuestionCard
            question="Do all zodiac signs make good rising signs?"
            answer="There's no 'good' or 'bad' rising sign. Each of the 12 zodiac signs rising sign brings unique strengths and challenges. The key is understanding and working with your rising sign energy."
          />
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <div className="bg-gradient-to-r from-[#D4AF37]/20 to-yellow-500/20 p-10 rounded-2xl border border-[#D4AF37]/50">
          <h2 className="text-3xl font-serif font-bold text-white mb-4">
            Calculate Your Zodiac Signs Rising Sign Now
          </h2>
          <p className="text-gray-300 mb-8 text-lg max-w-2xl mx-auto">
            Discover which of the 12 zodiac signs is YOUR rising sign. Get a complete birth chart analysis with planetary positions, houses, and aspects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0B0E14] font-bold rounded-lg hover:scale-105 transition-transform inline-flex items-center justify-center gap-2"
            >
              <Star className="w-5 h-5" />
              Calculate My Rising Sign →
            </Link>
            <Link
              to="/big-3-guide"
              className="px-8 py-4 bg-white/10 border border-white/20 text-white font-bold rounded-lg hover:bg-white/20 transition-colors"
            >
              Learn About the Big 3
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
}

function ImportanceCard({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
  return (
    <div className="bg-[#131722] p-6 rounded-xl border border-white/10 hover:border-[#D4AF37]/30 transition-all group">
      <div className="mb-4 group-hover:scale-110 transition-transform">{icon}</div>
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

function RisingSignCard({ data, index }: { data: any, index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="bg-gradient-to-br from-[#131722] to-[#0B0E14] p-6 rounded-xl border border-white/10 hover:border-[#D4AF37]/50 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center text-[#D4AF37]">
          {data.icon}
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-white mb-2">{data.sign}</h3>
          <div className="flex gap-4 text-sm mb-4">
            <span className="text-[#D4AF37]">Element: {data.element}</span>
            <span className="text-gray-500">•</span>
            <span className="text-purple-400">Ruler: {data.ruler}</span>
          </div>
          
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-400 mb-1">Personality:</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{data.personality}</p>
            </div>
            
            <div>
              <h4 className="text-sm font-semibold text-gray-400 mb-1">Physical Appearance:</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{data.appearance}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <h4 className="text-sm font-semibold text-green-400 mb-1">Strengths:</h4>
                <p className="text-gray-400 text-xs leading-relaxed">{data.strengths}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-orange-400 mb-1">Challenges:</h4>
                <p className="text-gray-400 text-xs leading-relaxed">{data.challenges}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function InteractionExample({ sunSign, risingSign, effect }: { sunSign: string, risingSign: string, effect: string }) {
  return (
    <div className="bg-black/30 p-5 rounded-lg border border-purple-500/20">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[#D4AF37] font-bold">{sunSign}</span>
        <span className="text-gray-500">+</span>
        <span className="text-purple-400 font-bold">{risingSign}</span>
      </div>
      <p className="text-gray-300 text-sm leading-relaxed">{effect}</p>
    </div>
  );
}

function MethodCard({ number, title, description, accuracy }: { number: number, title: string, description: string, accuracy: string }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center font-bold text-[#0B0E14]">
        {number}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <span className="text-xs px-3 py-1 bg-green-600/20 text-green-400 rounded-full">{accuracy}</span>
        </div>
        <p className="text-gray-300 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function QuestionCard({ question, answer }: { question: string, answer: string }) {
  return (
    <div className="bg-[#131722] p-6 rounded-xl border border-white/10">
      <h3 className="text-lg font-bold text-white mb-3 flex items-start gap-2">
        <span className="text-[#D4AF37]">Q:</span>
        {question}
      </h3>
      <p className="text-gray-300 text-sm leading-relaxed pl-6">{answer}</p>
    </div>
  );
}
